import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  SectionCard, StatCard, Button, EmptyState, PageLoader, StatusBadge,
  Skeleton, formatINR, cx,
} from "../components/UI";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import {
  MapPin, Wallet, TrendingUp, Receipt, Clock, Inbox, Fuel, IndianRupee,
  ArrowRight, Plus, ChartLine,
} from "lucide-react";

/* ── compact currency for tight KPI tiles ── */
const short = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  if (Math.abs(v) >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const MONTH_LABEL = (m) => {
  if (!m) return "";
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString("en-IN", { month: "short" });
};

/* ═══ Revenue / expense / profit trend ═══
   Plain SVG — no chart library in the bundle. Lines use non-scaling
   strokes so they stay 2px crisp however wide the card gets. */
function TrendChart({ data }) {
  if (!data?.length) {
    return <div className="h-48 grid place-items-center text-sm text-ink-400">No trend data yet</div>;
  }

  const W = 600, H = 180, PAD_X = 8, PAD_T = 12, PAD_B = 26;
  const maxVal = Math.max(...data.flatMap(d => [d.revenue || 0, d.expenses || 0, d.profit || 0]), 1);
  const step = data.length > 1 ? (W - PAD_X * 2) / (data.length - 1) : 0;
  const x = (i) => PAD_X + i * step;
  const y = (v) => PAD_T + (1 - Math.max(0, v || 0) / maxVal) * (H - PAD_T - PAD_B);

  const series = [
    { key: "revenue", label: "Revenue", color: "#10b981", fill: "url(#gRev)" },
    { key: "expenses", label: "Expenses", color: "#f59e0b", fill: null },
    { key: "profit", label: "Profit", color: "#2563eb", fill: null },
  ];

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44 sm:h-52 overflow-visible" preserveAspectRatio="none" role="img" aria-label="Six month revenue trend">
        <defs>
          <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* horizontal guides */}
        {[0, 0.5, 1].map(t => (
          <line key={t} x1={0} x2={W} y1={y(maxVal * t)} y2={y(maxVal * t)}
            stroke="#e6ebf2" strokeWidth="1" vectorEffect="non-scaling-stroke" strokeDasharray={t === 0 ? "" : "4 4"} />
        ))}

        {series.map(s => {
          const pts = data.map((d, i) => `${x(i)},${y(d[s.key])}`).join(" ");
          return (
            <g key={s.key}>
              {s.fill && <polygon points={`${x(0)},${y(0)} ${pts} ${x(data.length - 1)},${y(0)}`} fill={s.fill} />}
              <polyline points={pts} fill="none" stroke={s.color} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              {data.map((d, i) => (
                <circle key={i} cx={x(i)} cy={y(d[s.key])} r="3" fill="#fff" stroke={s.color} strokeWidth="2" vectorEffect="non-scaling-stroke">
                  <title>{`${MONTH_LABEL(d.month)} · ${s.label}: ${formatINR(d[s.key])}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>

      <div className="flex items-center justify-between mt-1 px-1">
        {data.map((d, i) => <span key={i} className="text-[11px] text-ink-400 font-medium">{MONTH_LABEL(d.month)}</span>)}
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 pt-3 border-t border-ink-100">
        {series.map(s => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-ink-500">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── labelled bar row used by the expense breakdown ── */
function BarRow({ label, value, max, color, icon }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const tones = {
    orange: "bg-orange-500", emerald: "bg-emerald-500", brand: "bg-brand-500",
    violet: "bg-violet-500", amber: "bg-amber-500", slate: "bg-ink-400",
  };
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="text-base w-5 shrink-0 text-center">{icon}</span>
      <span className="text-sm text-ink-600 capitalize w-24 sm:w-28 shrink-0 truncate">{label.replace(/_/g, " ")}</span>
      <div className="flex-1 h-2 rounded-full bg-ink-100 overflow-hidden min-w-[40px]">
        <div className={cx("h-full rounded-full transition-all duration-700", tones[color] || tones.brand)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-semibold text-ink-800 tnum w-20 sm:w-24 text-right shrink-0">{formatINR(value)}</span>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const month = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    Promise.all([api.get("/analytics/dashboard"), api.get("/analytics/revenue-trend")])
      .then(([d, t]) => { setData(d.data.data); setTrend(t.data.data || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-[122px] rounded-2xl" />)}
          </div>
          <PageLoader label="Loading analytics…" />
        </div>
      </AdminLayout>
    );
  }

  const d = data || {};
  const revenue = d.revenue?.monthly || 0;
  const totalExp = d.expenses?.monthly || 0;
  const profit = revenue - totalExp;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const expIcons = { fuel: "⛽", food: "🍱", salary: "💵", office_rent: "🏠", vehicle_maintenance: "🔧", other: "📦" };
  const expColors = { fuel: "orange", food: "emerald", salary: "brand", office_rent: "violet", vehicle_maintenance: "amber", other: "slate" };
  const byType = d.expenses?.byType || {};
  const maxExp = Math.max(...Object.values(byType), 1);

  const siteStats = [
    { label: "Active", value: d.sites?.active || 0, text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-100" },
    { label: "On Hold", value: d.sites?.["on-hold"] || 0, text: "text-amber-700", bg: "bg-amber-50 border-amber-100" },
    { label: "Completed", value: d.sites?.completed || 0, text: "text-brand-700", bg: "bg-brand-50 border-brand-100" },
    { label: "Inactive", value: d.sites?.inactive || 0, text: "text-ink-600", bg: "bg-ink-100 border-ink-200" },
  ];

  const funnel = [
    ["new", "New", "bg-brand-500"],
    ["contacted", "Contacted", "bg-indigo-500"],
    ["qualified", "Qualified", "bg-violet-500"],
    ["proposal_sent", "Proposal Sent", "bg-amber-500"],
    ["converted", "Converted", "bg-emerald-500"],
  ];
  const maxInq = Math.max(...funnel.map(([k]) => d.inquiries?.[k] || 0), 1);

  return (
    <AdminLayout>
      <div className="space-y-6 sm:space-y-7">

        {/* ══ Greeting ══ */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-ink-900 font-display">
              {greeting}, {user?.name?.split(" ")[0] || "Admin"} 👋
            </h1>
            <p className="text-ink-400 text-sm mt-1">
              {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button to="/admin/bills/new" icon={<Plus size={16} />} className="flex-1 sm:flex-none">New Invoice</Button>
            <Button to="/admin/inquiries" variant="secondary" icon={<Inbox size={16} />} className="flex-1 sm:flex-none">Inquiries</Button>
          </div>
        </div>

        {/* ══ KPIs ══ */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          <StatCard icon={<MapPin size={17} />} label="Active Sites" value={d.sites?.active || 0} color="blue" to="/admin/sites"
            sub={`${d.sites?.total || Object.values(d.sites || {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0)} total`} />
          <StatCard icon={<IndianRupee size={17} />} label="Revenue this month" value={short(revenue)} color="emerald" to="/admin/bills"
            sub={`${d.revenue?.billCount || 0} invoices`} />
          <StatCard icon={<Wallet size={17} />} label="Expenses this month" value={short(totalExp)} color="amber" to="/admin/expenses" />
          <StatCard icon={<TrendingUp size={17} />} label="Net profit" value={short(profit)} color={profit >= 0 ? "emerald" : "red"}
            sub={revenue > 0 ? `${((profit / revenue) * 100).toFixed(0)}% margin` : undefined} />
          <StatCard icon={<Clock size={17} />} label="Check-ins today" value={d.attendance?.today || 0} color="cyan" to="/admin/attendance" />
          <StatCard icon={<Receipt size={17} />} label="Pending bills" value={d.pendingBills || 0} color="amber" to="/admin/bills" />
          <StatCard icon={<Fuel size={17} />} label="Fuel this month" value={short(d.fuel?.monthly || 0)} color="orange" to="/admin/expenses"
            sub={`${(d.fuel?.litres || 0).toFixed(0)} L`} />
          <StatCard icon={<Inbox size={17} />} label="Open inquiries" value={(d.inquiries?.new || 0) + (d.inquiries?.contacted || 0)}
            color="purple" to="/admin/inquiries" sub="new + contacted" />
        </div>

        {/* ══ Trend + expense split ══ */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
          <SectionCard
            title="6-month trend"
            subtitle="Revenue, expenses and profit"
            icon={<ChartLine size={16} />}
            action={<Link to="/admin/bills" className="text-xs font-semibold text-brand-600 hover:text-brand-700 flex items-center gap-1">Bills <ArrowRight size={13} /></Link>}
            bodyClass="p-4 sm:p-5"
          >
            <TrendChart data={trend} />
          </SectionCard>

          <SectionCard
            title="Expense breakdown"
            subtitle={new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
            icon={<Wallet size={16} />}
            action={<Link to="/admin/expenses" className="text-xs font-semibold text-brand-600 hover:text-brand-700 flex items-center gap-1">Details <ArrowRight size={13} /></Link>}
            bodyClass="p-4 sm:p-5"
          >
            {!Object.keys(byType).length ? (
              <EmptyState compact icon={<Wallet size={22} />} title="No expenses yet" description={`Nothing recorded for ${month}.`} />
            ) : (
              <>
                <div className="divide-y divide-ink-50">
                  {Object.entries(byType).sort((a, b) => b[1] - a[1]).map(([type, val]) => (
                    <BarRow key={type} label={type} icon={expIcons[type] || "📦"} value={val} max={maxExp} color={expColors[type] || "brand"} />
                  ))}
                </div>
                <div className="flex justify-between items-center pt-3 mt-1 border-t border-ink-200">
                  <span className="text-sm font-medium text-ink-500">Total</span>
                  <span className="text-lg font-bold text-ink-900 font-display tnum">{formatINR(totalExp)}</span>
                </div>
              </>
            )}
          </SectionCard>
        </div>

        {/* ══ Profit by site + side widgets ══ */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
          <SectionCard
            title="Profit by site"
            subtitle="This month"
            icon={<MapPin size={16} />}
            action={<Link to="/admin/sites" className="text-xs font-semibold text-brand-600 hover:text-brand-700 flex items-center gap-1">All sites <ArrowRight size={13} /></Link>}
            bodyClass="p-3 sm:p-4"
          >
            {!d.profitBySite?.length ? (
              <EmptyState compact icon={<MapPin size={22} />} title="No billing data" description="Sites with invoices this month will appear here." />
            ) : (
              <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
                {d.profitBySite.map(s => (
                  <div key={s.siteId} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-ink-50/70 hover:bg-ink-100/70 transition-colors">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink-800 truncate">{s.siteName}</div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs mt-1">
                        <span className="text-emerald-600 tnum">In {formatINR(s.revenue, { compact: true })}</span>
                        <span className="text-red-500 tnum">Out {formatINR(s.expenses, { compact: true })}</span>
                      </div>
                    </div>
                    <div className={cx("text-sm font-bold font-display tnum shrink-0", s.profit >= 0 ? "text-emerald-600" : "text-red-600")}>
                      {s.profit >= 0 ? "+" : "−"}{formatINR(Math.abs(s.profit || 0), { compact: true })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <div className="space-y-4 sm:space-y-5">
            <SectionCard title="Sites overview" icon={<MapPin size={16} />} bodyClass="p-4">
              <div className="grid grid-cols-2 gap-3">
                {siteStats.map(s => (
                  <div key={s.label} className={cx("rounded-xl border p-3.5", s.bg)}>
                    <div className={cx("text-2xl font-bold font-display tnum leading-none", s.text)}>{s.value}</div>
                    <div className="text-xs text-ink-500 mt-1.5 font-medium">{s.label}</div>
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard
              title="Inquiry funnel"
              icon={<Inbox size={16} />}
              action={<Link to="/admin/inquiries" className="text-xs font-semibold text-brand-600 hover:text-brand-700 flex items-center gap-1">Manage <ArrowRight size={13} /></Link>}
              bodyClass="p-4 space-y-2.5"
            >
              {funnel.map(([key, label, cls]) => {
                const cnt = d.inquiries?.[key] || 0;
                return (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-xs text-ink-500 w-24 sm:w-28 shrink-0">{label}</span>
                    <div className="flex-1 h-2 rounded-full bg-ink-100 overflow-hidden">
                      <div className={cx("h-full rounded-full transition-all duration-700", cls)} style={{ width: `${Math.min(100, (cnt / maxInq) * 100)}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-ink-700 w-6 text-right tnum shrink-0">{cnt}</span>
                  </div>
                );
              })}
            </SectionCard>
          </div>
        </div>

        {/* ══ Recent invoices ══ */}
        <SectionCard
          title="Recent invoices"
          icon={<Receipt size={16} />}
          action={<Link to="/admin/bills" className="text-xs font-semibold text-brand-600 hover:text-brand-700 flex items-center gap-1">All invoices <ArrowRight size={13} /></Link>}
        >
          {!d.recentBills?.length ? (
            <EmptyState
              icon={<Receipt size={22} />}
              title="No invoices yet"
              description="Create your first invoice to start tracking revenue."
              action={<Button to="/admin/bills/new" icon={<Plus size={16} />}>New Invoice</Button>}
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {d.recentBills.map(bill => (
                <li key={bill._id}>
                  <Link to={`/admin/bills/${bill._id}`}
                    className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 hover:bg-ink-50/70 transition-colors group">
                    <span className="w-10 h-10 rounded-xl bg-ink-100 text-ink-500 grid place-items-center shrink-0 group-hover:bg-brand-50 group-hover:text-brand-600 transition-colors">
                      <Receipt size={16} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-ink-800 truncate">{bill.clientId?.name || "—"}</span>
                        <span className="font-mono text-[11px] text-ink-400">{bill.billNumber}</span>
                      </div>
                      <div className="text-xs text-ink-400 truncate mt-0.5">{bill.siteId?.name || bill.subject || "—"}</div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                      <span className="hidden xs:block"><StatusBadge status={bill.status} /></span>
                      <span className="text-sm font-bold text-ink-900 font-display tnum text-right">
                        {formatINR(bill.grandTotal, { compact: true })}
                      </span>
                      <ArrowRight size={15} className="text-ink-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all hidden sm:block" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </AdminLayout>
  );
}
